import React from "react";
import * as ReactDOM from "react-dom/client";
import "./landing-shared.entry.jsx";
const SiteNav = globalThis.SiteNav ?? globalThis.window?.SiteNav;
const SiteFooter = globalThis.SiteFooter ?? globalThis.window?.SiteFooter;
// Secure DNS deep-dive
const { useState } = React;

const Icon = {
  arrow: (p) => <svg width="12" height="12" viewBox="0 0 12 12" fill="none" {...p}><path d="M3 6h6m0 0L6 3m3 3L6 9" stroke="currentColor" strokeWidth="1.4" strokeLinecap="square"/></svg>,
  copy: (p) => <svg width="11" height="11" viewBox="0 0 12 12" fill="none" {...p}><rect x="3" y="3" width="7" height="7" stroke="currentColor" strokeWidth="1"/><path d="M2 8V2h6" stroke="currentColor" strokeWidth="1"/></svg>,
};

function Hero() {
  return (
    <section className="section section-dark pa-hero">
      <div className="dark-grid"></div>
      <div className="dark-glow"></div>
      <div className="container">
        <div className="reveal">
          <span className="pa-eyebrow"><span className="eb-dot"></span>SECURE DNS · <strong>Beta · Advanced protection layer</strong></span>
          <h1>Block ads, trackers,<br/>and the internet's<br/><em>worst neighborhoods</em>.</h1>
          <p className="pa-hero-sub">
            Per-device DNS profiles with category blocking, custom rules, and optional query logging. No agent on phones or tablets — just apply the resolver settings your platform supports and keep protection tied to the workspace.
          </p>
          <div className="pa-hero-ctas">
            <a href="https://app.named.network/signup" className="btn btn-accent btn-lg"><span>Set up my first profile <Icon.arrow /></span></a>
            <a href="#how" className="btn btn-on-dark btn-lg"><span>See how it works</span></a>
          </div>
          <div className="pa-hero-trust">
            <span><span className="check">✓</span>Works on iOS, Android, Wi-Fi, cellular</span>
            <span><span className="check">✓</span>No agent install on phones</span>
            <span><span className="check">✓</span>Encrypted DNS endpoints</span>
            <span><span className="check">✓</span>Logs you control or no logs at all</span>
          </div>
        </div>

        <div className="pa-stage reveal" style={{ animationDelay: '160ms' }}>
          <span className="pa-stage-label">LIVE QUERIES · profile: home · last 60s</span>
          <span className="pa-stage-corner tl"></span><span className="pa-stage-corner tr"></span>
          <span className="pa-stage-corner bl"></span><span className="pa-stage-corner br"></span>
          <BigStageViz />
        </div>

        <div className="pa-stats">
          {[
            { num: '14,832', label: 'Blocked today', desc: 'Across the example workspace, last 24h' },
            { num: '67%', label: 'Ad/tracker rate', desc: 'On a typical home device profile' },
            { num: '<5ms', label: 'Median resolve', desc: 'Example workspace latency view' },
            { num: '100%', label: 'Encrypted', desc: 'Encrypted resolver paths shown in this demo' },
          ].map((s, i) => (
            <div key={i} className="pa-stat reveal" style={{ animationDelay: `${i*80}ms` }}>
              <div className="num"><em>{s.num.match(/[\d.,]+/)?.[0] || s.num}</em>{s.num.replace(/[\d.,<]+/, '')}</div>
              <div className="lbl">{s.label}</div>
              <div className="desc">{s.desc}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function BigStageViz() {
  return (
    <svg viewBox="0 0 1200 460" style={{ width: '100%', height: 'auto', display: 'block', position: 'relative', zIndex: 1 }}>
      {/* Background dots */}
      {Array.from({length: 9}).map((_, r) =>
        Array.from({length: 18}).map((_, c) =>
          <circle key={`${r}-${c}`} cx={40 + c * 65} cy={40 + r * 50} r="0.7" fill="rgba(255,255,255,0.08)" />
        )
      )}

      {/* Devices (left) */}
      <g transform="translate(120, 230)">
        <text x="0" y="-160" fontFamily="var(--font-mono)" fontSize="10" fill="var(--text-dark-muted)" textAnchor="middle" letterSpacing="2">YOUR DEVICES</text>
        {[
          { y: -120, name: 'iphone-priya', os: 'iOS · profile home' },
          { y: -50, name: 'macbook-pro', os: 'macOS · profile work' },
          { y: 20, name: 'ipad-kids', os: 'iPadOS · profile family' },
          { y: 90, name: 'home-tv', os: 'tvOS · profile home' },
        ].map((d, i) => (
          <g key={i}>
            <rect x="-100" y={d.y - 22} width="200" height="44" rx="3" fill="var(--bg-dark-card)" stroke="var(--border-dark)" strokeWidth="1"/>
            <circle cx="-78" cy={d.y} r="4" fill="var(--accent)"/>
            <text x="-65" y={d.y - 4} fontFamily="var(--font-mono)" fontSize="10" fill="var(--text-dark-primary)">{d.name}</text>
            <text x="-65" y={d.y + 10} fontFamily="var(--font-mono)" fontSize="8" fill="var(--text-dark-muted)" letterSpacing="0.5">{d.os}</text>
          </g>
        ))}
      </g>

      {/* Center resolver */}
      <g transform="translate(600, 230)">
        <rect x="-180" y="-160" width="360" height="320" fill="none" stroke="var(--accent)" strokeWidth="1" strokeDasharray="4 6" opacity="0.5"/>
        <text x="-170" y="-140" fontFamily="var(--font-mono)" fontSize="10" fill="var(--accent)" letterSpacing="2">NAMED.NETWORK RESOLVER · DoH/DoT</text>

        <circle cx="0" cy="0" r="50" fill="var(--bg-dark-card)" stroke="var(--accent)" strokeWidth="1.5"/>
        <circle cx="0" cy="0" r="50" fill="var(--accent-fade)"/>
        <circle cx="0" cy="0" r="40" fill="none" stroke="var(--accent)" strokeWidth="0.5" strokeDasharray="2 3"/>
        <text x="0" y="-3" fontFamily="var(--font-mono)" fontSize="10" fill="var(--accent)" textAnchor="middle" letterSpacing="2">FILTER</text>
        <text x="0" y="11" fontFamily="var(--font-mono)" fontSize="8" fill="var(--accent)" textAnchor="middle" letterSpacing="1">match · block · allow</text>

        {/* Recent decisions */}
        <g transform="translate(0, 100)">
          {[
            { y: 0, d: 'doubleclick.net', verdict: 'BLOCKED · ads', col: 'var(--accent)' },
            { y: 22, d: 'github.com', verdict: 'ALLOWED', col: 'var(--success)' },
            { y: 44, d: 'tracking.io', verdict: 'BLOCKED · trackers', col: 'var(--accent)' },
          ].map((q, i) => (
            <g key={i}>
              <rect x="-160" y={q.y - 8} width="320" height="18" fill="var(--bg-dark)" stroke="var(--border-dark)" strokeWidth="0.5"/>
              <circle cx="-148" cy={q.y + 1} r="2.5" fill={q.col}/>
              <text x="-138" y={q.y + 4} fontFamily="var(--font-mono)" fontSize="9" fill="var(--text-dark-secondary)">{q.d}</text>
              <text x="148" y={q.y + 4} fontFamily="var(--font-mono)" fontSize="8" fill={q.col} textAnchor="end" letterSpacing="0.6">{q.verdict}</text>
            </g>
          ))}
        </g>
      </g>

      {/* Internet (right) */}
      <g transform="translate(1080, 230)">
        <text x="0" y="-160" fontFamily="var(--font-mono)" fontSize="10" fill="var(--text-dark-muted)" textAnchor="middle" letterSpacing="2">INTERNET</text>
        <rect x="-90" y="-120" width="180" height="240" fill="none" stroke="var(--border-dark-strong)" strokeWidth="1"/>
        {[
          { y: -90, name: 'github.com', good: true },
          { y: -50, name: 'cloudflare', good: true },
          { y: -10, name: 'doubleclick', good: false },
          { y: 30, name: 'tracking.io', good: false },
          { y: 70, name: 'malware-host', good: false },
        ].map((s, i) => (
          <g key={i}>
            <rect x="-78" y={s.y - 12} width="156" height="22" fill={s.good ? 'transparent' : 'var(--accent-fade)'} stroke={s.good ? 'var(--border-dark-subtle)' : 'var(--accent)'} strokeWidth="0.5" strokeDasharray={s.good ? 'none' : '2 3'}/>
            <text x="-72" y={s.y + 3} fontFamily="var(--font-mono)" fontSize="9" fill={s.good ? 'var(--text-dark-secondary)' : 'var(--accent)'} textDecoration={s.good ? 'none' : 'line-through'}>{s.name}</text>
            <text x="72" y={s.y + 3} fontFamily="var(--font-mono)" fontSize="8" fill={s.good ? 'var(--success)' : 'var(--accent)'} textAnchor="end" letterSpacing="0.5">{s.good ? '✓' : '✕'}</text>
          </g>
        ))}
      </g>

      {/* Lines */}
      <path d="M220 230 Q 400 230 540 230" className="route-line live" />
      <path d="M660 230 Q 870 230 990 230" className="route-line live" />
    </svg>
  );
}

function Workflow() {
  return (
    <section className="section section-dark" id="how">
      <div className="container">
        <div className="section-h">
          <span className="section-label">From zero to ad-free</span>
          <h2 style={{ color: 'var(--text-dark-primary)' }}>Four steps. Five minutes. No app on your phone.</h2>
          <p>DNS lives at a layer below applications, so you don't install anything on the device you're protecting. The OS does the talking — we just answer.</p>
        </div>
        <div className="workflow">
          {[
            { num: '01', dur: '~30s', title: 'Create a profile', body: 'Pick a name (home, work, family) and a base policy: balanced, strict, or custom.' },
            { num: '02', dur: '~1m', title: 'Pick categories', body: 'Ads, trackers, malware, adult, social, gambling — toggle each on or off.' },
            { num: '03', dur: '~1m', title: 'Add your own rules', body: 'Block specific domains, allow ones we caught wrongly, or whitelist a category for one device.' },
            { num: '04', dur: '~2m', title: 'Assign to devices', body: 'Assign the profile to a device and apply the resolver settings that platform supports.' },
          ].map((s, i) => (
            <div key={i} className="wf-step">
              <div className="wf-num"><span>STEP {s.num}</span><span className="dur">{s.dur}</span></div>
              <h4>{s.title}</h4>
              <p>{s.body}</p>
            </div>
          ))}
        </div>
        <div style={{ textAlign: 'center', marginTop: 48 }}>
          <a href="https://app.named.network/signup" className="btn btn-accent btn-lg"><span>Start protecting devices <Icon.arrow /></span></a>
        </div>
      </div>
    </section>
  );
}

function Categories() {
  const cats = [
    { num: '01', name: 'Ads', desc: 'Ad networks, served creatives, programmatic exchanges.', pct: 92 },
    { num: '02', name: 'Trackers', desc: 'Behavioural and analytics trackers across the web.', pct: 84 },
    { num: '03', name: 'Malware', desc: 'Known C2 hosts, phishing kits, malware delivery.', pct: 71 },
    { num: '04', name: 'Cryptomining', desc: 'In-browser miners and known mining pools.', pct: 56 },
    { num: '05', name: 'Adult', desc: 'Toggle for family profiles. Age-gated content.', pct: 48 },
    { num: '06', name: 'Gambling', desc: 'Casinos, sportsbooks, gambling-adjacent affiliates.', pct: 42 },
    { num: '07', name: 'Social media', desc: 'Optional category for focus / family profiles.', pct: 38 },
    { num: '08', name: 'AI scrapers', desc: 'Known training-data crawlers and scraper farms.', pct: 35 },
    { num: '09', name: 'Phishing', desc: 'Look-alike domains, brand impersonation.', pct: 64 },
  ];
  return (
    <section className="section section-light">
      <div className="container">
        <div className="section-h">
          <span className="section-label on-light">What you can block</span>
          <h2>Nine categories. Toggle what fits.</h2>
          <p>Representative categories from the current Secure DNS beta. Bars below are example workspace data.</p>
        </div>
        <div className="cat-grid">
          {cats.map((c, i) => (
            <div key={i} className="cat-tile">
              <span className="cat-num">{c.num}</span>
              <h4>{c.name}</h4>
              <p>{c.desc}</p>
              <div className="cat-bar"><div style={{ width: `${c.pct}%` }}></div></div>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--text-light-muted)' }}>{c.pct}% blocked rate</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Features() {
  return (
    <section className="section section-light-alt">
      <div className="container">
        <div className="section-h">
          <span className="section-label on-light">How it actually works</span>
          <h2>The boring details that matter.</h2>
          <p>DNS sounds simple but the security difference between resolvers is enormous. Here's the part where we get specific.</p>
        </div>

        <FeatureRow
          flip={false}
          tag="01 · PROFILES"
          title="One profile per situation, not per device."
          body="A 'family' profile blocks adult and gambling. A 'work' profile blocks social. A 'home' profile blocks ads and trackers. Assign each device to whichever profile fits — change instantly, no reconfiguration."
          bullets={[
            "Multiple profiles per workspace",
            "Profile-based assignment for different device groups",
            "Change a device assignment without recreating the profile",
            "Move a device between profiles in one click",
          ]}
          viz={<ProfileViz />}
        />

        <FeatureRow
          flip={true}
          tag="02 · RULES"
          title="Custom allow and block lists, no syntax."
          body="Block a domain because it's annoying you. Allow one we caught wrongly. Add subdomains, wildcards, or whole TLDs. We show you exactly what each rule will match before you save."
          bullets={[
            "Wildcard support: <strong>*.tracking.io</strong> blocks all subdomains",
            "Allow rules always win over block rules",
            "Profile-scoped rules with device assignment handled separately",
            "Built-in categories plus custom allow/block rules",
          ]}
          viz={<RulesViz />}
        />

        <FeatureRow
          flip={false}
          tag="03 · QUERY LOG"
          title="A log you control — or reduced visibility when you do not."
          body="Want to see what your devices are talking to? Turn logging on per profile. Want less retained detail? Leave it off. The resulting history stays in your workspace and only your workspace can read it."
          bullets={[
            "<strong>Per-profile log toggle</strong> — privacy is a choice",
            "Retention follows the current Secure DNS history policy",
            "Current beta emphasizes summary and recent history over deep forensics",
            "Blocked and allowed activity can feed workspace-level DNS visibility",
          ]}
          viz={<LogViz />}
        />

        <FeatureRow
          flip={true}
          tag="04 · DEVICE SETUP"
          title="Resolver settings for phones, laptops, and routers."
          body="named.network exposes the resolver settings current platforms need. Android can use a Private DNS hostname. Routers and homelab boxes can use DoT or DoH endpoints. Assigned Linux devices can consume the workspace bootstrap automatically."
          bullets={[
            "<strong>Assigned Linux devices:</strong> workspace bootstrap can apply the resolver path",
            "<strong>Android 9+:</strong> Private DNS hostname",
            "<strong>Routers:</strong> DoT for OpenWrt, AdGuard, Pi-hole upstream",
            "<strong>Browsers:</strong> Direct DoH URL for Firefox/Chrome",
          ]}
          viz={<SetupViz />}
        />
      </div>
    </section>
  );
}

function FeatureRow({ flip, tag, title, body, bullets, viz }) {
  return (
    <div className={`feature-row ${flip ? 'flip' : ''}`}>
      <div className="fr-copy reveal">
        <span className="section-label on-light">{tag}</span>
        <h3>{title}</h3>
        <p>{body}</p>
        <ul className="fr-bullets">
          {bullets.map((b, i) => <li key={i} dangerouslySetInnerHTML={{ __html: b }} />)}
        </ul>
      </div>
      <div className="reveal" style={{ animationDelay: '120ms' }}>{viz}</div>
    </div>
  );
}

function ProfileViz() {
  return (
    <div className="viz-frame" data-label="PROFILES · workspace acme-co">
      <span className="viz-corner tl"></span><span className="viz-corner tr"></span>
      <span className="viz-corner bl"></span><span className="viz-corner br"></span>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {[
          { name: 'home', cats: ['ads', 'trackers', 'malware'], devices: 6, active: true },
          { name: 'family', cats: ['ads', 'trackers', 'malware', 'adult', 'gambling'], devices: 4, active: false },
          { name: 'work', cats: ['ads', 'trackers', 'malware', 'social'], devices: 2, active: false },
          { name: 'guest', cats: ['malware'], devices: 1, active: false },
        ].map((p, i) => (
          <div key={i} style={{ padding: '14px 16px', background: p.active ? 'var(--accent-fade)' : 'var(--bg-light-card)', border: p.active ? '1px solid var(--accent)' : '1px solid var(--border-light)', borderRadius: 2 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 13, color: p.active ? 'var(--accent)' : 'var(--text-light-primary)' }}>{p.name}</span>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--text-light-muted)' }}>{p.devices} device{p.devices !== 1 ? 's' : ''}</span>
            </div>
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              {p.cats.map((c, j) => (
                <span key={j} style={{ fontFamily: 'var(--font-mono)', fontSize: 9, padding: '2px 8px', border: '1px solid var(--border-light)', borderRadius: 2, color: 'var(--text-light-secondary)' }}>{c}</span>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function RulesViz() {
  return (
    <div className="viz-frame" data-label="CUSTOM RULES · profile home">
      <span className="viz-corner tl"></span><span className="viz-corner tr"></span>
      <span className="viz-corner bl"></span><span className="viz-corner br"></span>
      <div style={{ marginBottom: 12, display: 'flex', gap: 6 }}>
        <input readOnly defaultValue="*.tracking-cdn.com" style={{ flex: 1, padding: '10px 12px', fontFamily: 'var(--font-mono)', fontSize: 12, background: 'var(--bg-light-card)', border: '1px solid var(--border-light)', borderRadius: 2 }}/>
        <select defaultValue="block" style={{ padding: '10px 12px', fontFamily: 'var(--font-mono)', fontSize: 11, background: 'var(--bg-light-card)', border: '1px solid var(--border-light)', borderRadius: 2 }}>
          <option>block</option><option>allow</option>
        </select>
        <button style={{ padding: '0 14px', fontFamily: 'var(--font-mono)', fontSize: 10, background: 'var(--accent)', color: '#fff', border: 'none', textTransform: 'uppercase', letterSpacing: '0.06em', cursor: 'pointer', borderRadius: 2 }}>Add</button>
      </div>
      {[
        { d: '*.doubleclick.net', a: 'block', s: 'category override', n: 'wildcard' },
        { d: 'fonts.gstatic.com', a: 'allow', s: 'manual', n: 'allowlist' },
        { d: 'crashlytics.com', a: 'block', s: 'manual', n: 'exact' },
        { d: '*.facebook.com', a: 'block', s: 'profile work', n: 'wildcard' },
      ].map((r, i) => (
        <div key={i} style={{ display: 'grid', gridTemplateColumns: '14px 1fr auto auto', gap: 10, alignItems: 'center', padding: '10px 12px', background: 'var(--bg-light-card)', border: '1px solid var(--border-light)', borderRadius: 2, marginBottom: 4 }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: r.a === 'block' ? 'var(--accent)' : 'var(--success)' }}></span>
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-light-primary)' }}>{r.d}</span>
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 9, color: 'var(--text-light-muted)' }}>{r.s}</span>
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 9, color: r.a === 'block' ? 'var(--accent)' : 'var(--success)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{r.a}</span>
        </div>
      ))}
    </div>
  );
}

function LogViz() {
  return (
    <div className="viz-frame" data-label="QUERY LOG · live" style={{ background: 'var(--bg-dark-alt)' }}>
      <span className="viz-corner tl"></span><span className="viz-corner tr"></span>
      <span className="viz-corner bl"></span><span className="viz-corner br"></span>
      <div style={{ background: 'var(--bg-dark-card)', border: '1px solid var(--border-dark)', borderRadius: 2 }}>
        {[
          { t: '15:42:08', d: 'doubleclick.net', dv: 'iphone-priya', v: 'BLOCK · ads', col: 'var(--accent)' },
          { t: '15:42:08', d: 'github.com', dv: 'macbook-pro', v: 'ALLOW', col: 'var(--success)' },
          { t: '15:42:09', d: 'tracking.io', dv: 'iphone-priya', v: 'BLOCK · trackers', col: 'var(--accent)' },
          { t: '15:42:09', d: 'cdn.jsdelivr.net', dv: 'macbook-pro', v: 'ALLOW', col: 'var(--success)' },
          { t: '15:42:10', d: 'malware-host.xyz', dv: 'home-tv', v: 'BLOCK · malware', col: 'var(--accent)' },
          { t: '15:42:10', d: 'api.openai.com', dv: 'macbook-pro', v: 'ALLOW', col: 'var(--success)' },
          { t: '15:42:11', d: 'fb.tracking.net', dv: 'ipad-kids', v: 'BLOCK · trackers', col: 'var(--accent)' },
          { t: '15:42:11', d: 'apple.com', dv: 'iphone-priya', v: 'ALLOW', col: 'var(--success)' },
        ].map((q, i, arr) => (
          <div key={i} style={{ display: 'grid', gridTemplateColumns: '50px 1fr 70px 90px', gap: 8, alignItems: 'center', padding: '7px 12px', borderBottom: i < arr.length - 1 ? '1px solid var(--border-dark-subtle)' : 'none', fontFamily: 'var(--font-mono)', fontSize: 10 }}>
            <span style={{ color: 'var(--text-dark-faint)' }}>{q.t}</span>
            <span style={{ color: 'var(--text-dark-secondary)' }}>{q.d}</span>
            <span style={{ color: 'var(--text-dark-muted)', fontSize: 9 }}>{q.dv}</span>
            <span style={{ color: q.col, fontSize: 9, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{q.v}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function SetupViz() {
  return (
    <div className="viz-frame" data-label="ADD A DEVICE · iphone-priya">
      <span className="viz-corner tl"></span><span className="viz-corner tr"></span>
      <span className="viz-corner bl"></span><span className="viz-corner br"></span>
      <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: 16, alignItems: 'center' }}>
        {/* QR */}
        <div style={{ width: 120, height: 120, background: '#fff', border: '1px solid var(--border-light)', padding: 8, borderRadius: 2 }}>
          <svg viewBox="0 0 21 21" style={{ width: '100%', height: '100%' }}>
            {Array.from({length: 21*21}).map((_, i) => {
              const r = Math.floor(i/21), c = i%21;
              // Position markers
              const pos = (r < 7 && c < 7) || (r < 7 && c > 13) || (r > 13 && c < 7);
              const fill = pos ? ((r === 0 || r === 6 || c === 0 || c === 6 || c === 14 || c === 20 || (r === 14) || (r === 20)) || ((r > 1 && r < 5 && c > 1 && c < 5) || (r > 1 && r < 5 && c > 15 && c < 19) || (r > 15 && r < 19 && c > 1 && c < 5))) : ((i * 7919) % 100 < 45);
              return fill ? <rect key={i} x={c} y={r} width="1" height="1" fill="#000"/> : null;
            })}
          </svg>
        </div>
        <div>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--text-light-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>iOS · macOS</div>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text-light-primary)', marginBottom: 4 }}>Scan to install profile</div>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--text-light-muted)', lineHeight: 1.5 }}>Example setup artifact · actual platform steps depend on the device path you use today.</div>
        </div>
      </div>
      <div style={{ marginTop: 16, padding: 12, background: 'var(--bg-dark)', borderRadius: 2, fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--text-dark-secondary)' }}>
        <div style={{ color: 'var(--text-dark-muted)', fontSize: 9, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>or paste · DoH URL</div>
        <div style={{ color: 'var(--accent)' }}>https://dns.named.network/profile/home/2k7af1c9</div>
      </div>
    </div>
  );
}

function MidCTA() {
  return (
    <section className="section section-light" style={{ padding: 0 }}>
      <div className="container">
        <div className="cta-inline">
          <div className="cta-inline-text">
            <h4>Block your first ad in five minutes.</h4>
            <p>Available as a current Secure DNS beta surface. No phone app required — apply the resolver settings your device supports and protection starts at the DNS layer.</p>
          </div>
          <a href="https://app.named.network/signup" className="btn btn-accent btn-lg"><span>Set up Secure DNS — free <Icon.arrow /></span></a>
        </div>
      </div>
    </section>
  );
}

function FAQ() {
  const faqs = [
    { q: 'Do you log my DNS queries?', a: 'Only if you turn logging on. The toggle lives per profile and the log is private to your workspace. We never sell, share, or aggregate. Default for new profiles is off.' },
    { q: 'Will this work on my phone without an app?', a: 'Yes. Current platforms can use encrypted DNS settings directly at the OS layer. named.network exposes the resolver settings you need for the device path you are using today.' },
    { q: 'How is this different from running Pi-hole?', a: "Pi-hole works at home. Secure DNS is designed around a hosted resolver path plus workspace-managed profiles and device assignment, so the same protection model can follow beyond one local network." },
    { q: "What if a category blocks something I need?", a: "Add an allow rule for that domain. Allow rules always win over category blocks and rule changes apply within seconds. Or carve out a per-device exception so other devices stay protected." },
    { q: 'Does it work for routers?', a: "Yes. We publish DoT and DoH endpoints any modern router can use as an upstream — OpenWrt, pfSense, AdGuard Home, Pi-hole. Route the whole household through one named.network profile." },
    { q: "Will Apple’s iCloud Private Relay break this?", a: "Platform behavior can vary, so this should be tested on the device path you care about. named.network provides the resolver settings and device assignment model; exact precedence still depends on the platform." },
    { q: "How do I block one website on one kid’s iPad?", a: "Create a ‘family’ profile, add a custom block rule for that domain, assign just that iPad to the profile. Other devices keep their own settings." },
    { q: 'Is this real beta or beta-forever beta?', a: "Real beta. Profiles, rules, categories, logs, device assignment — all working today. The features still cooking are: scheduled profiles, blocklist source editor, and a richer analytics view." },
  ];
  const [open, setOpen] = useState(0);
  return (
    <section className="section section-light">
      <div className="container-narrow">
        <div className="section-h">
          <span className="section-label on-light">FAQ</span>
          <h2>The questions we get most.</h2>
        </div>
        <div className="faq-list">
          {faqs.map((f, i) => (
            <div key={i} className={`faq-item ${open === i ? 'open' : ''}`}>
              <button className="faq-q" onClick={() => setOpen(open === i ? -1 : i)}>
                <span>{f.q}</span><span className="plus">+</span>
              </button>
              <div className="faq-a">{f.a}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FinalCTA() {
  return (
    <section className="section section-dark" style={{ padding: '128px 0' }}>
      <div className="dark-grid"></div>
      <div className="dark-glow"></div>
      <div className="container">
        <div className="cta-final">
          <span className="section-label">One more thing</span>
          <h2>Quieter internet,<br/>one profile away.</h2>
          <p>Secure DNS in its current beta gives you profiles, rules, device assignment, and encrypted resolver endpoints. Set it up tonight, notice the difference tomorrow.</p>
          <div className="ctas" style={{ marginTop: 36 }}>
            <a href="https://app.named.network/signup" className="btn btn-accent btn-lg"><span>Create my workspace <Icon.arrow /></span></a>
            <a href="https://app.named.network/login" className="btn btn-on-dark btn-lg"><span>Sign in</span></a>
          </div>
          <div style={{ marginTop: 24, fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-dark-faint)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
            Beta · Requires a named.network workspace
          </div>
        </div>
      </div>
    </section>
  );
}

function App() {
  return (
    <>
      <SiteNav />
      <Hero />
      <Workflow />
      <Categories />
      <Features />
      <MidCTA />
      <FAQ />
      <FinalCTA />
      <SiteFooter />
    </>
  );
}

ReactDOM.createRoot(document.getElementById('root')).render(<App />);

